import Badge from '@repo/ui/badge'
import { useQuery } from '@tanstack/react-query'
import { forwardRef, useImperativeHandle } from 'react'

import { getAffectedStaffs } from '../../../api/affected-staffs/get-affected-staffs'
import { getCenterEnrollments } from '../../../api/centers/get-center-enrollments'
import useTranslate from '../../../lib/hooks/use-translate'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { EnrollmentDiffusionStateEnum } from '../../../lib/types/models/enrollment-center/enrollment-center'

type Props = {
  id: number
  isEnrollmentDiffused?: boolean
}

export type OlCenterPlacementSummaryBadgesRef = {
  refetch: () => void
}

const OlCenterPlacementSummaryBadges = forwardRef<OlCenterPlacementSummaryBadgesRef, Props>(
  ({ id, isEnrollmentDiffused = true }, ref) => {
    const { t } = useTranslate()

    // Only count students whose presence is the last state diffused to the OL: a student
    // whose removal has been diffused must leave these totals without waiting for the
    // "acknowledge withdrawal" action. For admins the backend ignores this filter and
    // counts on the LEC side (deleted = false) instead.
    const { data: countStudentsCenter, refetch: refetchCountStudentsCenter } = useQuery({
      queryKey: [
        'center-enrollments',
        id,
        { count: true, filter: { diffusionStates: [EnrollmentDiffusionStateEnum.EXISTING] } }
      ],
      queryFn: () =>
        getCenterEnrollments(id, {
          count: true,
          filter: { diffusionStates: [EnrollmentDiffusionStateEnum.EXISTING] }
        })
    })

    const { data: countPlacedStudentsCenter, refetch: refetchCountPlacedStudentsCenter } =
      useQuery({
        queryKey: [
          'center-enrollments',
          id,
          {
            count: true,
            filter: {
              hasHostFamily: true,
              diffusionStates: [EnrollmentDiffusionStateEnum.EXISTING]
            }
          }
        ],
        queryFn: () =>
          getCenterEnrollments(id, {
            count: true,
            filter: {
              hasHostFamily: true,
              diffusionStates: [EnrollmentDiffusionStateEnum.EXISTING]
            }
          })
      })

    // Only the AFFECTED staffs are leaders of the center, like the leaders table: a
    // cancelled affectation keeps its staff request and host family but is not a leader.
    const staffsFilter = { centerId: id, statuses: [AffectedStaffStatusEnum.AFFECTED] }
    const { data: countStaffsCenter, refetch: refetchCountStaffsCenter } = useQuery({
      queryKey: ['affected-staffs', { count: true, filter: staffsFilter }],
      queryFn: () => getAffectedStaffs({ count: true, filter: staffsFilter })
    })

    const { data: countPlacedStaffstsCenter, refetch: refetchCountPlacedStaffstsCenter } =
      useQuery({
        queryKey: [
          'affected-staffs',
          { count: true, filter: { ...staffsFilter, hasHostFamily: true } }
        ],
        queryFn: () =>
          getAffectedStaffs({
            count: true,
            filter: { ...staffsFilter, hasHostFamily: true }
          })
      })

    useImperativeHandle(ref, () => ({
      refetch: () => {
        void refetchCountStudentsCenter()
        void refetchCountPlacedStudentsCenter()
        void refetchCountStaffsCenter()
        void refetchCountPlacedStaffstsCenter()
      }
    }))

    const countNonPlacedStudentsCenter =
      (countStudentsCenter?.total ?? 0) - (countPlacedStudentsCenter?.total ?? 0)
    const countNonPlacedStaffsCenter =
      (countStaffsCenter?.total ?? 0) - (countPlacedStaffstsCenter?.total ?? 0)
    return (
      <div className="flex gap-2 items-center">
        {isEnrollmentDiffused && (
          <Badge
            text={`${t('ol.center.placement.badge.students.in.center.title')} : ${countStudentsCenter?.total ?? 0} (${t('ol.center.placement.badge.placed')} : ${countPlacedStudentsCenter?.total ?? 0} - ${t('ol.center.placement.badge.not.placed')} : ${countNonPlacedStudentsCenter})`}
            variant="fushia"
          />
        )}
        <Badge
          text={`${t('ol.center.placement.badge.staffs.in.center.title')} : ${countStaffsCenter?.total ?? 0} (${t('ol.center.placement.badge.placed')} : ${countPlacedStaffstsCenter?.total ?? 0} - ${t('ol.center.placement.badge.not.placed')} : ${countNonPlacedStaffsCenter})`}
          variant="blue"
        />
      </div>
    )
  }
)

OlCenterPlacementSummaryBadges.displayName = 'OlCenterPlacementSummaryBadges'

export default OlCenterPlacementSummaryBadges
